import { ApplicationData, REGISTRANT_OPTIONS } from 'src/types/flow';
import { APIPerson, FormModelPerson, Person } from 'src/types/person';
import { UserDetails } from 'src/types/auth';

export const apiPersonToFormModel = (person: APIPerson): FormModelPerson => {
  const completePhysicalAddress = person.misc?.physicalAddress
    ? {
        value: person.misc.physicalAddress,
        label: person.misc.physicalAddress,
        components: person.misc.physicalAddressComponents,
      }
    : undefined;
  return {
    ...person,
    ...(completePhysicalAddress ? { completePhysicalAddress } : {}),
  };
};

export const initialPerson: Person = {
  firstName: '',
  lastName: '',
  companyName: '',
  email: '',
  phone: '',
  mailingAddress: '',
};

const initialFormValues: FormModelPerson = {
  ...initialPerson,
  sameAsPropertyAddress: false,
};

export const getInitialFormModelValues = (
  initialValue?: FormModelPerson
): FormModelPerson => {
  return {
    ...initialFormValues,
    ...initialValue,
  };
};

export const mapOwnersToFormModel = (
  owners: APIPerson[],
  propertyAddress: string = ''
): FormModelPerson[] => {
  return owners.filter(Boolean).map((owner) => ({
    ...apiPersonToFormModel(owner),
    sameAsPropertyAddress: Boolean(
      owner.mailingAddress &&
        owner.mailingAddress.toLowerCase() === propertyAddress.toLowerCase()
    ),
  }));
};

export const mapFormModelToPerson = ({
  sameAsPropertyAddress,
  ...rest
}: FormModelPerson): Person => ({
  ...rest,
});

export const userDetailsToPerson = (user: UserDetails): Person => ({
  companyName: user.companyName ?? '',
  email: user.email,
  firstName: user.firstName ?? '',
  lastName: user.lastName ?? '',
  mailingAddress: user.mailingAddress ?? '',
  phone: user.phone ?? '',
});

export interface RegistrantFormInitialState {
  owners: FormModelPerson[];
  localRole: REGISTRANT_OPTIONS;
  propertyManager: Person | undefined;
}

export const initializeRegistrantFormState = (
  data: ApplicationData | undefined,
  loggedInUserDetails: Person
): RegistrantFormInitialState => {
  const registrantType = data?.registrant?.registrantType;
  // Determine the initial role
  const initialRole =
    data?.registrant?.registrantType ?? REGISTRANT_OPTIONS.OWNER;

  // Determine initial owners array
  // If data has registrant owners, use them (excluding the first one which is the main registrant)
  // Otherwise, use empty array (in creation mode, the logged-in user will be added as the first owner)
  const initialOwners = mapOwnersToFormModel(data?.registrant?.owner ?? []);
  let initialPropertyManager;
  if (
    registrantType === REGISTRANT_OPTIONS.PROPERTY_MANAGER ||
    registrantType === REGISTRANT_OPTIONS.PROPERTY_MANAGER_INDEPENDENT
  ) {
    initialPropertyManager = data?.registrant?.registrant;
  } else {
    initialPropertyManager = loggedInUserDetails;
  }

  return {
    owners: initialOwners,
    localRole: initialRole,
    propertyManager: initialPropertyManager,
  };
};
